import { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import UnapprovedPaymentsTableFilters from './components/UnapprovedPaymentsTableFilters/UnapprovedPaymentsTableFilters';
import UnapprovedPaymentsTable from './components/UnapprovedPaymentsTable';
import MultiPaymentConfirmations from './components/MultiPaymentConfirmations/MultiPaymentConfirmations';
import PaymentConfirmation from './components/PaymentConfirmation/PaymentConfirmation';
import { BackButton } from 'components/common/BackButton/BackButton';
import { CUSTOMER_PERMISSIONS, BACKEND_MODULE_TYPES } from 'components/common/constants';
import Loader from 'components/common/Loader';
import Pagination from 'components/common/Pagination/Pagination';
import PaymentForm from 'components/common/PaymentForm';
//eslint-disable-next-line max-len
import PopUpDeleteTransactionConfirmationScheme from 'components/common/PopUpScheme/PopUpDeleteTransactionConfirmationScheme';
//eslint-disable-next-line max-len
import PopUpForApprovalSuccessScheme from 'components/common/PopUpScheme/PopUpForApprovalSuccessScheme';
import PopUpInternalActionConfirmationScheme from 'components/common/PopUpScheme/PopUpInternalActionConfirmationScheme';
import i18nContext from 'components/i18n-context';
import { Container } from 'uikit/Container/Container';
import Button from 'uikit/Button/Button';
import { PopUp } from 'uikit/PopUp/PopUp';
import './UnapprovedPayments.scss';
import { NewContainer } from '../../uikit/NewContainer/NewContainer';

const popUpInitialData = {
  isShowPopUp: false,
  popUpType: '',
  title: '',
  description: '',
  confirmButtonLabel: '',
  cancelButtonLabel: '',
  onCancel: () => {},
  onConfirm: () => {}
};

const POP_UP_TYPE = {
  DELETE: 'DELETE',
  APPROVE: 'APPROVE',
  SUCCESSFUL_CONFIRMATION: 'SUCCESSFUL_CONFIRMATION'
};

const PAGE_CONTENT_TYPE = {
  TABLE: 'TABLE',
  APPROVAL: 'APPROVAL',
  EDIT: 'EDIT'
};

const UnapprovedPayments = ({ customerNumber, userAccounts, hasPermissions, paymentStore }) => {
  const i18n = useContext(i18nContext);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [popUpData, setPopUpData] = useState(popUpInitialData);
  const [pageContentType, setPageContentType] = useState(PAGE_CONTENT_TYPE.TABLE);
  const isUserCanApproveTransaction = hasPermissions([CUSTOMER_PERMISSIONS.CREATE_TRANSACTION]);

  useEffect(() => {
    if (customerNumber) {
      paymentStore.getPreparedTransactions(customerNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerNumber,
    paymentStore.preparedTransactionsFilters.from_date,
    paymentStore.preparedTransactionsFilters.to_date,
    paymentStore.preparedTransactionsFilters.currency,
    paymentStore.preparedTransactionsFilters.wallet_number,
    paymentStore.preparedTransactionsFilters.search_text,
    paymentStore.preparedTransactionsPagination.size,
    paymentStore.preparedTransactionsPagination.page
  ]);

  useEffect(() => {
    if (customerNumber && paymentStore.isPreparedTransactionsSuccessfullyDeleted) {
      paymentStore.getPreparedTransactions(customerNumber);

      paymentStore.setIsPreparedTransactionsSuccessfullyDeleted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, paymentStore.isPreparedTransactionsSuccessfullyDeleted]);

  useEffect(() => {
    if (paymentStore.isShowingInternalActionConfirmation) {
      setPopUpData({
        isShowPopUp: true,
        popUpType: POP_UP_TYPE.APPROVE,
        title: i18n.getMessage('unapprovedPayments.popUp.approveTransaction.title'),
        confirmButtonLabel: i18n.getMessage('unapprovedPayments.popUp.approveTransaction.confirm'),
        onConfirm: async (code) => await paymentStore.confirmPreparedTransactions(customerNumber, code),
        onCancel: () => {
          paymentStore.setConfirmationId(null);
          paymentStore.setIsShowingInternalActionConfirmation(false);
          resetPopUpData();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStore.isShowingInternalActionConfirmation]);

  useEffect(() => {
    if (paymentStore.isPreparedTransactionsSuccessfullyConfirmed) {
      resetPopUpData();

      setPopUpData({
        isShowPopUp: true,
        popUpType: POP_UP_TYPE.SUCCESSFUL_CONFIRMATION,
        title: i18n.getMessage('unapprovedPayments.popUp.successfulConfirmation.title'),
        description: i18n.getMessage('unapprovedPayments.popUp.successfulConfirmation.description'),
        confirmButtonLabel: i18n.getMessage('unapprovedPayments.popUp.successfulConfirmation.confirm'),
        onCancel: async () => {
          await paymentStore.getPreparedTransactions(customerNumber);
          paymentStore.setIsPreparedTransactionsSuccessfullyConfirmed(false);
          resetPopUpData();
          setPageContentType(PAGE_CONTENT_TYPE.TABLE);
          setSelectedTransactions([]);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStore.isPreparedTransactionsSuccessfullyConfirmed]);

  const handleEdit = async (transactionId) => {
    await paymentStore.getPreparedTransactionDataForEdit(customerNumber, transactionId, userAccounts);
    setPageContentType(PAGE_CONTENT_TYPE.EDIT);
  };

  const handleSelect = (selectedTransaction, isSelected) => {
    setSelectedTransactions((prev) =>
      isSelected
        ? [...prev, selectedTransaction]
        : prev.filter((transaction) => transaction.id !== selectedTransaction.id)
    );
  };

  const resetPopUpData = () => {
    setPopUpData(popUpInitialData);
    paymentStore.setIsShowingInternalActionConfirmation(false);
  };

  const handleConfirmTransactionsDeletion = async (transactionId) => {
    setPopUpData({
      isShowPopUp: true,
      popUpType: POP_UP_TYPE.DELETE,
      title: i18n.getMessage('unapprovedPayments.popUp.deleteTransaction.title'),
      description: i18n.getMessage('unapprovedPayments.popUp.deleteTransaction.description'),
      confirmButtonLabel: i18n.getMessage('unapprovedPayments.popUp.deleteTransaction.confirm'),
      cancelButtonLabel: i18n.getMessage('unapprovedPayments.popUp.deleteTransaction.cancel'),
      onConfirm: async () => {
        await paymentStore.deletePreparedTransactions(customerNumber, [transactionId]);

        resetPopUpData();
      },
      onCancel: resetPopUpData
    });
  };

  const handleSubmitPreparedTransaction = async () => {
    const selectedTransactionsIds = selectedTransactions.map((transaction) => transaction.id);

    await paymentStore.submitPreparedTransactions(customerNumber, selectedTransactionsIds);
  };

  const handleApproveButton = () => {
    setPageContentType(PAGE_CONTENT_TYPE.APPROVAL);
  };

  const handleBackButton = () => {
    if (pageContentType === PAGE_CONTENT_TYPE.EDIT) {
      paymentStore.resetPreparedTransactionData();
    }
    setPageContentType(PAGE_CONTENT_TYPE.TABLE);
    paymentStore.resetPreparedTransactionData();
  };

  const resetPreparedTransactionData = async () => {
    paymentStore.resetPreparedTransactionData();
    await paymentStore.getPreparedTransactions(customerNumber);
    setPageContentType(PAGE_CONTENT_TYPE.TABLE);
  };

  const handleSetCurrentPage = async (pageNumber) => {
    paymentStore.setPreparedTransactionsPage(pageNumber);
  };

  const handleSetPageSize = (size) => {
    paymentStore.setPreparedTransactionsPageSize(size);
  };

  return (
    <section className={'page-container'}>
      {pageContentType === PAGE_CONTENT_TYPE.TABLE && (
        <NewContainer
          className={'unapproved-payments content-card-wrapper'}
          header={i18n.getMessage('container.forApproval')}
        >
          {!paymentStore.isPreparedTransactionsFetched ? (
            <div className={'unapproved-payments-loader'}>
              <Loader />
            </div>
          ) : (
            <>
              <div className={'actions-container'}>
                <div className={'action-buttons'}>
                  <Button
                    type={'primary'}
                    size={'large'}
                    className={'approve-button'}
                    isDisabled={!selectedTransactions.length || !isUserCanApproveTransaction}
                    onClick={handleApproveButton}
                    isLoading={paymentStore.isPreparedTransactionsLoading}
                  >
                    {i18n.getMessage('unapprovedPayments.button.approve')}
                  </Button>
                </div>
              </div>
              <UnapprovedPaymentsTableFilters
                filters={paymentStore.preparedTransactionsFilters}
                userAccounts={userAccounts}
                onFilterChange={paymentStore.setPreparedTransactionsFilter}
                handleSetDateFilter={paymentStore.setPreparedTransactionsFilter}
                handleSearchText={({ target: { name, value } }) => {
                  paymentStore.setPreparedTransactionsFilter(name, value);
                  paymentStore.getPreparedTransactionsWithDebounce(customerNumber);
                }}
              />
              {paymentStore?.preparedTransactions?.length === 0 ? (
                <div className={'empty-prepared-transactions-wrapper'}>
                  <h2 className={'empty-prepared-transactions-header'}>
                    {i18n.getMessage('unapprovedPayments.emptyTransactions.header')}
                  </h2>
                  <p className={'empty-prepared-transactions-text'}>
                    {i18n.getMessage('unapprovedPayments.emptyTransactions.description')}
                  </p>
                </div>
              ) : (
                <>
                  <UnapprovedPaymentsTable
                    transactions={paymentStore.preparedTransactions}
                    selectedTransactions={selectedTransactions}
                    onRemove={handleConfirmTransactionsDeletion}
                    onEdit={handleEdit}
                    onSelect={handleSelect}
                    isLoading={paymentStore.isPreparedTransactionsLoading}
                    userAccounts={userAccounts}
                  />
                  <Pagination
                    isShowPaginationSize={true}
                    paginationSize={paymentStore.preparedTransactionsPagination.size}
                    handleChangePaginationSize={handleSetPageSize}
                    isLoading={paymentStore.isLoading}
                    currentPage={paymentStore.preparedTransactionsPagination.page}
                    numberOfPages={paymentStore.preparedTransactionsTotalPages}
                    setCurrentPage={handleSetCurrentPage}
                  />
                </>
              )}
            </>
          )}
        </NewContainer>
      )}

      {pageContentType === PAGE_CONTENT_TYPE.APPROVAL && selectedTransactions.length === 1 && (
        <NewContainer
          className={'content-card-wrapper'}
          header={i18n.getMessage('container.forApprovalReviewAnConfirm')}
          isBackButton={true}
          handleBackButtonClick={handleBackButton}
        >
          <PaymentConfirmation
            transaction={selectedTransactions[0]}
            onEdit={handleEdit}
            onConfirm={handleSubmitPreparedTransaction}
            isLoading={paymentStore.isPreparedTransactionsLoading}
            customerNumber={customerNumber}
            setSelectedTransaction={setSelectedTransactions}
          />
        </NewContainer>
      )}

      {pageContentType === PAGE_CONTENT_TYPE.APPROVAL && selectedTransactions.length > 1 && (
        <NewContainer
          className={'content-card-wrapper'}
          header={i18n.getMessage('container.forApprovalReviewAnConfirm')}
          isBackButton={true}
          handleBackButtonClick={handleBackButton}
        >
          <MultiPaymentConfirmations
            transactions={selectedTransactions}
            onConfirm={handleSubmitPreparedTransaction}
            isLoading={paymentStore.isPreparedTransactionsLoading}
            customerNumber={customerNumber}
            setSelectedTransaction={setSelectedTransactions}
          />
        </NewContainer>
      )}

      {pageContentType === PAGE_CONTENT_TYPE.EDIT && (
        <PaymentForm
          customerNumber={customerNumber}
          isForApprovalTransactionEdit={true}
          resetPreparedTransactionData={resetPreparedTransactionData}
          backToForApprovalTable={() => setPageContentType(PAGE_CONTENT_TYPE.TABLE)}
        />
      )}

      <PopUp className={'prepared-transactions'} show={popUpData.isShowPopUp} onClose={resetPopUpData}>
        <>
          {popUpData.popUpType === POP_UP_TYPE.DELETE && (
            <PopUpDeleteTransactionConfirmationScheme
              title={popUpData.title}
              description={popUpData.description}
              closeButtonLabel={popUpData.cancelButtonLabel}
              confirmButtonLabel={popUpData.confirmButtonLabel}
              onClose={popUpData.onCancel}
              onConfirm={popUpData.onConfirm}
            />
          )}

          {popUpData.popUpType === POP_UP_TYPE.APPROVE && (
            <PopUpInternalActionConfirmationScheme
              isLoading={paymentStore.isPreparedTransactionsLoading}
              isShowSwitchConfirmationMethodScreen={paymentStore.isShowSwitchConfirmationMethodScreen}
              title={popUpData.title}
              confirmButtonLabel={popUpData.confirmButtonLabel}
              onClose={popUpData.onCancel}
              onConfirm={popUpData.onConfirm}
              error={paymentStore.confirmationPopupError}
              clearError={() => paymentStore.clearConfirmationPopupError()}
              resendSecurityCode={() => paymentStore.resendPaymentSecurityCode()}
              setIsShowSwitchConfirmationMethodScreen={(status) =>
                paymentStore.setIsShowSwitchConfirmationMethodScreen(status)
              }
              handleForceSwitchToAnotherConfirmationType={paymentStore.forceSwitchToAnotherConfirmationType}
              confirmationId={paymentStore.confirmationId}
              module={BACKEND_MODULE_TYPES.PAYMENT}
            />
          )}

          {popUpData.popUpType === POP_UP_TYPE.SUCCESSFUL_CONFIRMATION && (
            <PopUpForApprovalSuccessScheme
              title={popUpData.title}
              description={popUpData.description}
              confirmButtonLabel={popUpData.confirmButtonLabel}
              onClose={popUpData.onCancel}
            />
          )}
        </>
      </PopUp>
    </section>
  );
};

UnapprovedPayments.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  hasPermissions: PropTypes.func,
  paymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  customerNumber: stores.userStore.userData.account?.account_number,
  userAccounts: stores.userStore.userAccounts,
  hasPermissions: stores.userStore.hasPermissions,
  paymentStore: stores.paymentStore
}))(observer(UnapprovedPayments));
